/* =============================================================
   HALLOW'S EVE — landing page enhancements
   Vanilla JS. No dependencies, no globals beyond one load guard.

   Everything here is progressive enhancement. If this file never runs
   (blocked, errored, JS disabled) the page is still complete:
     • all content is visible — the reveal CSS is only armed by the
       class this script adds
     • the hero atmosphere simply sits still
     • the moon holds a static crescent and the candle never lights

   Cart handling deliberately lives in this theme's shared fmr-cart.js,
   not here: it owns [data-fmr-cart-form] and dispatches Horizon's
   `cart:update` so the drawer opens. Two interceptors on one form would
   add the item twice. With no JS at all the form still POSTs normally to
   routes.cart_add_url.

   Respects prefers-reduced-motion, avoids layout shift (opacity,
   transform and filter only), and re-initialises for the Shopify
   theme editor via shopify:section:load.

   Selectors, classes and data attributes are all namespaced with the
   lp-hallows-eve prefix so nothing can collide with the theme.
   ============================================================= */
(function () {
  'use strict';

  // One <script> tag is rendered per section — run the work once.
  if (window.__lpHallowsEveLoaded) return;
  window.__lpHallowsEveLoaded = true;

  var ROOT = '.lp-hallows-eve';
  var READY_ATTR = 'data-lp-hallows-eve-ready';
  var CLASS_ENHANCED = 'lp-hallows-eve--enhanced';
  var CLASS_REVEAL_READY = 'lp-hallows-eve--reveal-ready';
  var CLASS_VISIBLE = 'is-visible';

  var reduceQuery = window.matchMedia
    ? window.matchMedia('(prefers-reduced-motion: reduce)')
    : null;
  var prefersReduced = !!(reduceQuery && reduceQuery.matches);
  var supportsObserver = 'IntersectionObserver' in window;

  function each(list, fn) {
    Array.prototype.forEach.call(list || [], fn);
  }

  /* ---------- Scroll reveals -------------------------------- */
  /* Slower and longer-thrown than a typical fade: the page is meant to
     resolve out of the dark rather than pop. */

  var revealObserver = null;

  function getRevealObserver() {
    if (revealObserver) return revealObserver;
    revealObserver = new IntersectionObserver(
      function (entries) {
        each(entries, function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add(CLASS_VISIBLE);
          revealObserver.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
    );
    return revealObserver;
  }

  function armReveals(root) {
    if (prefersReduced || !supportsObserver) return;
    root.classList.add(CLASS_REVEAL_READY);

    each(root.querySelectorAll('[data-lp-hallows-eve-reveal]'), function (item, index) {
      // Gentle stagger, capped so later items never feel laggy.
      item.style.transitionDelay = Math.min(index * 90, 420) + 'ms';
      getRevealObserver().observe(item);
    });
  }

  /* ---------- Hero atmosphere drift ------------------------ */
  /* Publishes a single 0→1 progress value as a CSS custom property; the
     stylesheet decides what moves. One passive scroll listener,
     coalesced into a rAF, and only while the hero is actually on
     screen — so it costs nothing for the rest of the page. */

  function armHeroDrift(root) {
    if (prefersReduced || !supportsObserver) return;

    var hero = root.querySelector('[data-lp-hallows-eve-hero]');
    if (!hero || !('requestAnimationFrame' in window)) return;

    var frame = 0;
    var onScreen = false;

    function update() {
      frame = 0;
      var rect = hero.getBoundingClientRect();
      var span = rect.height || 1;
      // 0 while the hero's top is at the viewport top, 1 once it has
      // scrolled a full hero height past it.
      var progress = Math.min(Math.max(-rect.top / span, 0), 1);
      hero.style.setProperty('--lp-hallows-eve-scroll', progress.toFixed(4));
    }

    // Cancel-and-replace rather than a boolean latch. A latch that only the
    // frame callback clears sticks forever if that frame never runs — which is
    // exactly what happens when the tab is hidden or rAF is throttled, and it
    // silently kills the effect for the rest of the session.
    function onScroll() {
      if (!onScreen) return;
      if (frame) window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(update);
    }

    new IntersectionObserver(
      function (entries) {
        each(entries, function (entry) {
          onScreen = entry.isIntersecting;
          if (onScreen) onScroll();
        });
      },
      { threshold: 0 }
    ).observe(hero);

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    update();
  }

  /* ---------- Page progress (waxing moon) ------------------ */
  /* Publishes one 0->1 value on :root for the whole page, so the moon in every
     section fills as the visitor travels toward the closing CTA. Custom
     properties inherit, so a single write reaches every moon — no per-section
     bookkeeping and no observer per moon.

     Armed once, not per section: prepareRoot runs for each lp-hallows-eve
     section on the page, and this is page-level state. */

  var progressArmed = false;

  function armPageProgress() {
    if (prefersReduced || progressArmed) return;
    if (!('requestAnimationFrame' in window)) return;
    // Nothing to drive: leave the property unset so the CSS default applies.
    if (!document.querySelector('.lp-hallows-eve__moon--wax')) return;
    progressArmed = true;

    var root = document.documentElement;
    var frame = 0;

    function update() {
      frame = 0;
      // A short page has nothing to scroll — never divide by zero.
      var scrollable = root.scrollHeight - window.innerHeight;
      var progress = scrollable > 0 ? window.pageYOffset / scrollable : 0;
      if (progress < 0) progress = 0;
      if (progress > 1) progress = 1;
      root.style.setProperty('--lp-hallows-eve-page-progress', progress.toFixed(4));
    }

    // See the note in armHeroDrift: cancel-and-replace, never a latch.
    function onScroll() {
      if (frame) window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(update);
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    // Coming back to a backgrounded tab, recompute immediately rather than
    // waiting for the next scroll — the moon should already be right.
    document.addEventListener('visibilitychange', function () {
      if (!document.hidden) update();
    });
    update();
  }

  /* ---------- Cursor candlelight --------------------------- */
  /* Only for a real, fine pointer: on a touch screen there is nothing to
     follow and the glow would sit parked in the middle of the hero. The blob
     stays at opacity 0 until the first move, so it fades in where the cursor
     actually is instead of sliding out from the centre. */

  function armCandle(root) {
    if (prefersReduced) return;

    var candle = root.querySelector('[data-lp-hallows-eve-candle]');
    if (!candle || !('requestAnimationFrame' in window)) return;

    var fine = window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)');
    if (!fine || !fine.matches) return;

    var host = candle.closest('.lp-hallows-eve') || root;
    var x = 0;
    var y = 0;
    var frame = 0;

    function paint() {
      frame = 0;
      candle.style.setProperty('--lp-hallows-eve-cursor-x', x + 'px');
      candle.style.setProperty('--lp-hallows-eve-cursor-y', y + 'px');
      candle.classList.add('is-lit');
    }

    host.addEventListener(
      'pointermove',
      function (event) {
        if (event.pointerType === 'touch') return;
        var rect = host.getBoundingClientRect();
        x = event.clientX - rect.left;
        y = event.clientY - rect.top;
        if (frame) window.cancelAnimationFrame(frame);
        frame = window.requestAnimationFrame(paint);
      },
      { passive: true }
    );

    host.addEventListener('pointerleave', function () {
      candle.classList.remove('is-lit');
    });
  }

  /* ---------- Boot ---------------------------------------- */

  function prepareRoot(root) {
    if (root.getAttribute(READY_ATTR) === 'true') return;
    root.setAttribute(READY_ATTR, 'true');
    root.classList.add(CLASS_ENHANCED);

    armReveals(root);
    armHeroDrift(root);
    armCandle(root);
    armPageProgress();
  }

  function prepare(scope) {
    if (!scope) return;

    if (scope.matches && scope.matches(ROOT)) {
      prepareRoot(scope);
      return;
    }

    if (!scope.querySelectorAll) return;
    each(scope.querySelectorAll(ROOT), prepareRoot);
  }

  function init() {
    prepare(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-arm sections added or re-rendered inside the theme editor.
  document.addEventListener('shopify:section:load', function (event) {
    prepare(event.target);
  });
})();
